import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import {PropertyId, SpeechRecognizer, TranslationRecognizer} from 'microsoft-cognitiveservices-speech-sdk';
import {Input, Language, Output, TranslationResult, TranslationsBody} from './Configuration';
import {HybridModelText2SpeechProcessor} from './HybridModelText2SpeechProcessor';
import {CancellationReason} from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports';
import {UUID} from 'crypto';

const azureRegion = 'eastus';


export default class HybridModelSessionContext {

    private sessionId: UUID;
    private selectedOutputs: Output[];
    private selectedInput: Input;
    private inputLangKey: string;

    private readonly speechRecognitionConfig: sdk.SpeechTranslationConfig;

    private speechRecognizer: SpeechRecognizer | undefined;
    private translationRecognizer: TranslationRecognizer | undefined;

    //private translationProcessor: TranslationProcessor;
    private readonly speech2TextProcessorByLanguage: Map<string, HybridModelText2SpeechProcessor>;
    private state: 'READY' | 'RUNNING' | 'STOPPED';

    public setSpeechToken(newToken: string) {
        console.log('Refreshing Azure Token... Text 2 Speech');
        this.speech2TextProcessorByLanguage?.forEach(processor => {
            processor.setSpeechToken(newToken);
        });
        console.log('Refresh Successful Text 2 Speech');

        if (this.speechRecognizer) {
            console.log('Refreshing Azure Token... Recognition');
            this.speechRecognizer.authorizationToken = newToken;
            console.log('Refresh Successful Recognition');
        }

        if (this.translationRecognizer) {
            console.log('Refreshing Azure Token... translationRecognizer');
            this.translationRecognizer.authorizationToken = newToken;
            console.log('Refresh Successful translationRecognizer');
        }
    }

    constructor(azureSpeechAPIToken: string, sessionId: UUID, selectedInput: Input, selectedOutputs: Output[],
                onPartialRecognitionReady: (recognizedText: string) => void,
                onRecognitionReady: (recognizedText: string) => void,
                onPartialTranslationReady: (translationReady: Map<Language, TranslationResult>) => void,
                onTranslationReady: (translationsBody: TranslationsBody) => void,
                onSessionCanceled: (sessionId: UUID, azureSessionId: string, reason: CancellationReason) => void) {

        this.sessionId = sessionId;
        this.selectedInput = selectedInput;
        this.inputLangKey = selectedInput.language.recognitionKey.split('-')[0];
        this.selectedOutputs = selectedOutputs;

        console.log('[HybridMode_Session Context] Constructor')
        console.log('[HybridMode_Session Context] Selected Input', this.selectedInput)
        console.log('[HybridMode_Session Context] Selected Output', this.selectedOutputs)

        /*this.translationProcessor = this.startTranslationProcessor(
            translationKey,
            this.selectedOutputs.map(output => output.language),
            this.selectedInput.language,
            onTranslationReady
        );*/

        console.log('[HybridMode_Session Context] Starting Text2Speech processors...');
        this.speech2TextProcessorByLanguage = new Map<string, HybridModelText2SpeechProcessor>();
        this.selectedOutputs.forEach(output => {
            this.speech2TextProcessorByLanguage.set(output.language.translationKey, this.startText2SpeechProcessor(output, azureSpeechAPIToken));
            console.log('[Session Context] Text2Speech processor started for lang key: ', output.language.speakKey);
        });
        console.log('[HybridMode_Session Context] Completed Text2Speech processors...');

        //const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(azureSubscriptionKey, azureRegion);
        console.log('[HybridMode_Session Context] Starting Voice Recognition and Translation processors...');
        const audioConfig = sdk.AudioConfig.fromMicrophoneInput(this.selectedInput.device.deviceId);

        this.speechRecognitionConfig = sdk.SpeechTranslationConfig.fromAuthorizationToken(azureSpeechAPIToken, azureRegion);
        //this.speechRecognitionConfig.enableDictation();
        this.speechRecognitionConfig.setProperty(PropertyId.Speech_SegmentationSilenceTimeoutMs, '400');
        this.speechRecognitionConfig.outputFormat = sdk.OutputFormat.Detailed;

        //Pame
        // this.speechRecognitionConfig.voiceName = this.selectedOutputs[0].language.voices[0];

        this.selectedOutputs.forEach(output => {
            console.log('[HybridMode_Session Context] Adding translation target language: ', output);
            this.speechRecognitionConfig.addTargetLanguage(output.language.translationKey);
        });

        // if (selectedInput.autoDetect) {
        //     const autoDetectSourceLanguageConfig = sdk.AutoDetectSourceLanguageConfig.fromLanguages(
        //         ["en-US", "de-DE", "zh-CN"] //TODO fix lista
        //     );
        //
        //     // this.speechRecognitionConfig.speechRecognitionLanguage = "en-US";
        //     console.log("-----------------------")
        //     console.log("autoDetectSourceLanguageConfig.properties", autoDetectSourceLanguageConfig.properties)
        //     console.log("speechTranslationConfigImpl.properties", (this.speechRecognitionConfig as SpeechTranslationConfigImpl).properties)
        //
        //
        //     /*
        //         const speechTranslationConfigImpl: SpeechTranslationConfigImpl = speechTranslationConfig as SpeechTranslationConfigImpl;
        //         autoDetectSourceLanguageConfig.properties.mergeTo(speechTranslationConfigImpl.properties);
        //         return new TranslationRecognizer(speechTranslationConfig, audioConfig);
        //     */
        //     this.speechRecognizer = sdk.TranslationRecognizer.FromConfig(this.speechRecognitionConfig, autoDetectSourceLanguageConfig, audioConfig)
        //
        //
        // } else {
        this.speechRecognitionConfig.speechRecognitionLanguage = this.selectedInput.language.recognitionKey; //en-US
        if (this.selectedOutputs.length === 0) {
            this.speechRecognizer = new sdk.SpeechRecognizer(this.speechRecognitionConfig, audioConfig);

            this.speechRecognizer.recognizing = (sender, event) => {
                let partiallyRecognizedText = event.result.text;
                onPartialRecognitionReady(partiallyRecognizedText);
            }

            this.speechRecognizer.recognized = (sender, event) => {
                //Stable recognition - execute recognitionReady callback
                let recognizedText = event.result.text;
                onRecognitionReady(recognizedText);
            }

            this.speechRecognizer.canceled = (sender, event) => {
                onSessionCanceled(this.sessionId, event.sessionId, event.reason);
            }

        } else {
            this.translationRecognizer = new sdk.TranslationRecognizer(this.speechRecognitionConfig, audioConfig);

            //Handle unstable recognition
            this.translationRecognizer.recognizing = (sender, event) => {

                console.log('translationRecognizer.recognizing: ', event);

                if (event.result.reason === sdk.ResultReason.NoMatch) {
                    console.log('[Session Context] Skipping event Recognition result reason: ', event.result.reason, event);
                    return;
                }

                let partiallyRecognizedText = event.result.text;
                onPartialRecognitionReady(partiallyRecognizedText);

                let parsedTranslations = new Map<Language, TranslationResult>();
                event.result.translations.languages.forEach(translationKey => {
                        let partialTranslation = event.result.translations.get(translationKey);
                        const lang = {id: translationKey} as Language;
                        // console.log("SENT raw 2", tr)
                        const translationResult = {
                            originalText: partiallyRecognizedText || '',
                            translatedText: partialTranslation,
                            status: '',
                            language: lang
                        } as TranslationResult;
                        parsedTranslations.set(lang, translationResult);

                        // SessionContext.processPartialRecognition(partialTranslation, text => {
                        //     this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(text);
                        // })
                        console.log('[Sending PARTIAL to speech2TextProcessorByLanguage -> ', translationResult.language.id, this.speech2TextProcessorByLanguage)
                        this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(event.result.offset + '', partialTranslation, false);
                    }
                )

                onPartialTranslationReady(parsedTranslations);
            }

            //When recognition is ready do something
            this.translationRecognizer.recognized = (sender, event) => {

                console.log('translationRecognizer.recognized: ', event)

                if (event.result.reason === sdk.ResultReason.NoMatch) {
                    console.log('[Session Context] Skipping event Recognition result reason: ', event.result.reason, event);
                    return;
                }

                //Stable recognition - execute recognitionReady callback
                let recognizedText = event.result.text;
                onRecognitionReady(recognizedText);

                //Get the translations
                let parsedTranslations = new Map<Language, TranslationResult>();
                let translations: TranslationsBody = {inputs: {}, outputs: {}};
                event.result.translations.languages.forEach(translationKey => {
                        let stableTranslation = event.result.translations.get(translationKey);
                        const lang = {id: translationKey} as Language;
                        const translationResult = {
                            originalText: recognizedText || '',
                            translatedText: stableTranslation,
                            status: '',
                            language: lang
                        } as TranslationResult;

                        //Build a list of all translations by language
                        parsedTranslations.set(lang, translationResult);
                        translations.outputs[translationKey] = stableTranslation;
                        if (!translations.inputs[this.inputLangKey])
                            translations.inputs[this.inputLangKey] = recognizedText;

                        //Enqueue to Text 2 Speech
                        console.log('[Sending STABLE to speech2TextProcessorByLanguage -> ', translationResult.language.id, this.speech2TextProcessorByLanguage)

                        //this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.processRecognition(partialTranslation, text => {
                        this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(event.result.offset + '', stableTranslation, true);
                        //});
                    }
                )

                //Stable translation - execute recognitionReady callback
                onTranslationReady(translations);
            }

            //para testear
            this.translationRecognizer.sessionStarted = (sender, event) => {
                console.log('[Session Context] Voice Recognition and Translation processors started. Event -> ', event);
            }
            this.translationRecognizer.sessionStopped = (sender, event) => {
                console.log('[sessionStopped]...', event);
            }
            this.translationRecognizer.canceled = (sender, event) => {
                console.error('[Session Context] Voice Recognition and Translation processors canceled...', event);
                onSessionCanceled(this.sessionId, event.sessionId, event.reason);
            }

        }
        this.state = 'READY';
        console.log('[Session Context] All processors are installed. Session context is ready to start recognition.');
    }

    public start() {
        //this.translationProcessor.start();
        this.speech2TextProcessorByLanguage?.forEach(processor => {
            processor.start()
        });
        this.speechRecognizer?.startContinuousRecognitionAsync();
        this.translationRecognizer?.startContinuousRecognitionAsync();
        this.state = 'RUNNING';
        console.log('[Session Context] Started...');

        // return new Promise<void>(() => {});
    }

    public stop() {
        console.log('[Session Context] [2] Stop ')
        this.speechRecognizer?.stopContinuousRecognitionAsync();
        this.speechRecognizer?.close();
        this.translationRecognizer?.stopContinuousRecognitionAsync();
        this.translationRecognizer?.close();
        this.speech2TextProcessorByLanguage?.forEach((processor, index) => {
            console.log('[Session Context] [3] Stopping text2speech processor: ' + processor.getOutputConfig() + ' + (' + index + ')')
            processor.stop();
        })
        //this.translationProcessor.stop();
        this.state = 'STOPPED';
        console.log('[Session Context] [5] Stopped')
    }

    public getState() {
        return this.state;
    }

    private startText2SpeechProcessor = (outputConfig: Output, azureSpeechAPIToken: string)
        /*onSpeechAudioReady: (speechAudio: { lang: string, text: string }[]) => void,
        onSpeechAudioPlaybackCompleted: (speechAudio: { lang: string, text: string }[]) => void*/
        : HybridModelText2SpeechProcessor => {
        return new HybridModelText2SpeechProcessor(outputConfig, azureSpeechAPIToken);
    }

    public getText2Speech(translationKey: string) {
        return this.speech2TextProcessorByLanguage?.get(translationKey);
    }
}


